// Ospiti degli affitti brevi (Cortirola + Nuovo Fiore): paese, lingua, contatti e l'elenco «come Booking».
// Funzioni pure: le usano le rotte in short-rental-api.ts e i test.

// Codici ISO 3166-1 a due lettere: il sito mostra il nome del paese nella lingua della pagina (Intl.DisplayNames).
export const COUNTRY_CODES = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP " +
  "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT " +
  "MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
  "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG " +
  "UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW"
).split(" ");
const COUNTRY_SET = new Set(COUNTRY_CODES);

export function isCountryCode(value: string) {
  return COUNTRY_SET.has(value);
}

// Lingue che l'ospite può indicare come preferite (le email automatiche esistono in it/en/de/fr).
export const GUEST_LANGUAGES = ["it", "en", "de", "fr", "es", "pt", "nl", "pl", "ru", "ro", "sv", "cs", "zh", "ja", "ar"] as const;
export type GuestLanguage = typeof GUEST_LANGUAGES[number];

export const LANGUAGE_NAMES: Record<GuestLanguage, string> = {
  it: "Italiano", en: "Inglese", de: "Tedesco", fr: "Francese", es: "Spagnolo", pt: "Portoghese", nl: "Olandese", pl: "Polacco",
  ru: "Russo", ro: "Rumeno", sv: "Svedese", cs: "Ceco", zh: "Cinese", ja: "Giapponese", ar: "Arabo",
};

// Bandierina emoji dal codice del paese (due «lettere regionali» Unicode).
export function countryFlag(code: string) {
  if (!/^[A-Z]{2}$/.test(code)) return "";
  return String.fromCodePoint(...[...code].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65));
}

export function countryName(code: string) {
  if (!code) return "";
  try { return new Intl.DisplayNames(["it"], { type: "region" }).of(code) || code; } catch { return code; }
}

// Prefissi internazionali dei paesi più frequenti: servono per aprire WhatsApp quando l'ospite scrive il numero senza +.
const CALLING_CODES: Record<string, string> = {
  IT: "39", SM: "378", VA: "39", DE: "49", AT: "43", CH: "41", FR: "33", BE: "32", NL: "31", LU: "352", GB: "44", IE: "353",
  ES: "34", PT: "351", DK: "45", SE: "46", NO: "47", FI: "358", PL: "48", CZ: "420", SK: "421", HU: "36", RO: "40", BG: "359",
  GR: "30", HR: "385", SI: "386", RS: "381", AL: "355", UA: "380", RU: "7", TR: "90", IL: "972", US: "1", CA: "1", MX: "52",
  BR: "55", AR: "54", AU: "61", NZ: "64", JP: "81", CN: "86", KR: "82", IN: "91", AE: "971", ZA: "27", EE: "372", LV: "371", LT: "370", MT: "356", CY: "357",
};

// Numero per wa.me: solo cifre, con il prefisso internazionale.
// «+49 151…» e «0049 151…» restano come sono; un numero senza prefisso prende quello del paese dell'ospite
// (senza lo 0 iniziale, tranne in Italia dove lo 0 dei fissi fa parte del numero). Senza paese si assume l'Italia.
export function whatsappNumber(phone: string, country = "") {
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";
  if (trimmed.startsWith("+")) return digits;
  if (digits.startsWith("00")) return digits.slice(2);
  const code = CALLING_CODES[country] || (country ? "" : "39");
  if (!code) return digits;
  if (code === "39") return digits.startsWith("39") && digits.length > 10 ? digits : `39${digits}`;
  if (digits.startsWith(code) && digits.length > 10) return digits;
  return `${code}${digits.replace(/^0+/, "")}`;
}

export type GuestStatus = "in_house" | "arriving" | "stayed";

export type RentalGuestStay = {
  requestId: string; siteId: string; house: string; checkIn: string; checkOut: string; nights: number; guests: number; bookedAt: string;
};

export type RentalGuestEntry = {
  key: string; name: string; country: string; countryName: string; flag: string; language: string;
  phone: string; email: string; whatsapp: string; status: GuestStatus; stay: RentalGuestStay; stays: number; history: RentalGuestStay[];
};

export type GuestSourceRow = {
  id: string; siteId: string; checkIn: string; checkOut: string; guests: number; createdAt: string; status: string;
  country: string; language: string; name: string; phone: string; email: string;
};

function statusOf(stay: Pick<RentalGuestStay, "checkIn" | "checkOut">, today: string): GuestStatus {
  if (stay.checkIn <= today && today < stay.checkOut) return "in_house";
  if (stay.checkIn > today) return "arriving";
  return "stayed";
}

const STATUS_ORDER: Record<GuestStatus, number> = { in_house: 0, arriving: 1, stayed: 2 };

// Elenco «come Booking»: solo soggiorni confermati (le prenotazioni pagate sono sempre confermate), annullate escluse.
// Lo stesso ospite (stessa email o stesso telefono) compare una volta sola, con il numero di soggiorni.
// Per ogni ospite si mostra il soggiorno che conta adesso: quello in corso, altrimenti il prossimo, altrimenti l'ultimo.
export function buildRentalGuestList(rows: GuestSourceRow[], houses: Map<string, string>, today: string): RentalGuestEntry[] {
  // Union-find: una riga si unisce a tutte quelle con la stessa email O lo stesso telefono, anche a catena
  // (A/P, B/Q, A/Q → un solo ospite). Telefono: ultime 10 cifre, come nella rubrica.
  const confirmed = rows.filter((row) => row.status === "confirmed");
  const parent = confirmed.map((_, index) => index);
  const find = (index: number): number => { while (parent[index] !== index) { parent[index] = parent[parent[index]]; index = parent[index]; } return index; };
  const union = (a: number, b: number) => { const rootA = find(a); const rootB = find(b); if (rootA !== rootB) parent[Math.max(rootA, rootB)] = Math.min(rootA, rootB); };
  const firstByKey = new Map<string, number>();
  confirmed.forEach((row, index) => {
    const email = row.email.trim().toLowerCase();
    const digits = row.phone.replace(/\D/g, "");
    for (const key of [email ? `mail:${email}` : "", digits.length >= 6 ? `tel:${digits.slice(-10)}` : ""]) {
      if (!key) continue;
      const seen = firstByKey.get(key);
      if (seen === undefined) firstByKey.set(key, index); else union(seen, index);
    }
  });
  const byRoot = new Map<number, GuestSourceRow[]>();
  confirmed.forEach((row, index) => { const root = find(index); byRoot.set(root, [...(byRoot.get(root) || []), row]); });
  const groups = [...byRoot.values()];
  const entries = groups.map((group, index): RentalGuestEntry => {
    const history = group.map((row): RentalGuestStay => ({
      requestId: row.id, siteId: row.siteId, house: houses.get(row.siteId) || "Casa", checkIn: row.checkIn, checkOut: row.checkOut,
      nights: Math.round((Date.parse(`${row.checkOut}T12:00:00Z`) - Date.parse(`${row.checkIn}T12:00:00Z`)) / 86400000),
      guests: row.guests, bookedAt: row.createdAt,
    })).sort((a, b) => b.checkIn.localeCompare(a.checkIn));
    const current = history.find((stay) => statusOf(stay, today) === "in_house");
    const next = history.filter((stay) => stay.checkIn > today).sort((a, b) => a.checkIn.localeCompare(b.checkIn))[0];
    const stay = current || next || history[0];
    // Dati di contatto più recenti (l'ospite può aver cambiato numero o lingua).
    const latest = [...group].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    const pick = (field: "name" | "phone" | "email" | "country" | "language") => latest.find((row) => row[field])?.[field] || "";
    const country = pick("country");
    const phone = pick("phone");
    return {
      key: `ospite-${index + 1}`, name: pick("name"), country, countryName: countryName(country), flag: countryFlag(country),
      language: pick("language"), phone, email: pick("email"), whatsapp: phone ? whatsappNumber(phone, country) : "",
      status: statusOf(stay, today), stay, stays: history.length, history,
    };
  });
  return entries.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    || (a.status === "arriving" ? a.stay.checkIn.localeCompare(b.stay.checkIn) : b.stay.checkIn.localeCompare(a.stay.checkIn)));
}
